/**
 * Xác thực OIDC (Keycloak, PKCE) — TDD §13.2: access token 15 phút, refresh XOAY VÒNG.
 *
 * T7-E (29/09/2026, T27-05 🔐 người dùng chốt): token (access + refresh + id) CHỈ nằm trong BỘ NHỚ (`InMemoryWebStorage`) — không sessionStorage/
 * localStorage/cookie, nên một đoạn script lạ (XSS) không đọc được refresh token đã lưu ở lần tải sau. Keycloak xoay refresh token (`revokeRefreshToken`,
 * `refreshTokenMaxReuse 0`): mỗi lần làm mới nhận refresh token MỚI, token cũ dùng lại → Keycloak từ chối + gỡ phiên client, listener báo iam-svc thu hồi mọi phiên.
 * Tải lại trang = mất token → chuyển tới Keycloak; phiên SSO (≤ 8 h, cookie của Keycloak) còn thì quay lại ngay không hỏi mật khẩu.
 * sessionStorage chỉ giữ trạng thái PKCE ngắn hạn (`stateStore`) và đường dẫn quay lại `kovix.returnTo` — không bí mật lâu dài.
 *
 * Không tự đọc claim để quyết định quyền: quyền lấy từ iam-svc (/me/permissions) để cùng nguồn với API (BRD §9.10).
 */
import {
  InMemoryWebStorage,
  type User,
  UserManager,
  type UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { useI18n } from './i18n';
import { jwtPayload, mfaStateOf } from './mfa';

const tenantAuthority: string = import.meta.env.VITE_OIDC_AUTHORITY ?? 'http://localhost:8080/realms/kovix-tenant';
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID ?? 'admin-console';

/**
 * Realm đăng nhập của tab (06/10/2026 — người dùng chọn phương án "đường dẫn riêng"): nhân sự tenant = `kovix-tenant` (mặc định);
 * quản trị viên nền tảng vào `/platform…` → `kovix-platform` (cùng client `admin-console`, MFA bắt buộc). Lựa chọn giữ trong sessionStorage của TAB
 * (chỉ tên realm — không phải token) để callback `/auth/callback`, tải lại trang và phiên hỗ trợ (đang ở trang tenant) vẫn đúng realm; đăng xuất / đóng tab = về mặc định.
 */
export type LoginRealm = 'tenant' | 'platform';
export const LOGIN_REALM_KEY = 'kovix.loginRealm';
export const PLATFORM_ENTRY = '/platform';

export function isPlatformPath(pathname: string): boolean {
  return pathname === PLATFORM_ENTRY || pathname.startsWith(`${PLATFORM_ENTRY}/`);
}

/** Vào bằng `/platform…` → platform; còn lại theo lựa chọn đã lưu của tab (giá trị lạ = tenant). */
export function resolveLoginRealm(pathname: string, stored: string | null | undefined): LoginRealm {
  if (isPlatformPath(pathname)) return 'platform';
  return stored === 'platform' ? 'platform' : 'tenant';
}

/** `…/realms/kovix-tenant[/…]` → cùng gốc, realm `kovix-platform` (authority tuyệt đối hay tương đối `/auth/realms/…` đều được). */
export function forRealm(url: string, realm: LoginRealm): string {
  return realm === 'platform' ? url.replace(/\/realms\/[^/]+/, '/realms/kovix-platform') : url;
}

function initialRealm(): LoginRealm {
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(LOGIN_REALM_KEY);
  } catch {
    /* storage bị chặn */
  }
  const realm = resolveLoginRealm(window.location.pathname, stored);
  try {
    if (realm === 'platform') window.sessionStorage.setItem(LOGIN_REALM_KEY, 'platform');
  } catch {
    /* storage bị chặn — callback sẽ không biết realm: ensureSignedIn vẫn đúng vì quay lại /platform */
  }
  return realm;
}

/** Realm của tab hiện tại — quyết định MỘT lần khi nạp trang (UserManager dựng theo nó). */
export const loginRealm: LoginRealm = initialRealm();
const authority = forRealm(tenantAuthority, loginRealm);

/** Khóa sessionStorage giữ đường dẫn sâu để quay lại sau đăng nhập (không bí mật). */
export const RETURN_TO_KEY = 'kovix.returnTo';
/** Tiền tố khóa oidc-client-ts dùng cho người dùng đã đăng nhập (bản cũ lưu ở sessionStorage). */
export const LEGACY_USER_KEY_PREFIX = 'oidc.user:';

/**
 * Thiết lập OIDC thuần (test được): người dùng/token vào `InMemoryWebStorage`; `stateStorage` (sessionStorage) chỉ nhận trạng thái PKCE của một lần chuyển hướng.
 * `automaticSilentRenew` dùng refresh token (grant refresh_token, không iframe) trước hạn access token 60 s.
 */
export function oidcSettings(o: {
  authority: string;
  clientId: string;
  origin: string;
  stateStorage: Storage;
  metadata?: UserManagerSettings['metadata'];
  /** Ngôn ngữ trang đăng nhập Keycloak (`ui_locales`) — thắng `Accept-Language` của trình duyệt (03/11/2026: trình duyệt tiếng Anh vẫn ra trang Việt). */
  uiLocales?: string;
}): UserManagerSettings {
  const s: UserManagerSettings = {
    authority: o.authority,
    client_id: o.clientId,
    redirect_uri: `${o.origin}/auth/callback`,
    post_logout_redirect_uri: o.origin,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    stateStore: new WebStorageStateStore({ store: o.stateStorage }),
  };
  if (o.metadata) s.metadata = o.metadata;
  if (o.uiLocales) s.ui_locales = o.uiLocales;
  return s;
}

/** Xóa token do bản trước T7-E để lại trong Web Storage (nâng cấp tại chỗ — tab mở sẵn không còn refresh token nằm ở storage). */
export function purgeLegacyTokenStorage(...stores: Array<Storage | null | undefined>): number {
  let removed = 0;
  for (const st of stores) {
    if (!st) continue;
    try {
      const keys: string[] = [];
      for (let i = 0; i < st.length; i++) {
        const k = st.key(i);
        if (k?.startsWith(LEGACY_USER_KEY_PREFIX)) keys.push(k);
      }
      for (const k of keys) {
        st.removeItem(k);
        removed++;
      }
    } catch {
      /* storage bị chặn (chế độ riêng tư) — không có gì để dọn */
    }
  }
  return removed;
}

function safeStorage(kind: 'sessionStorage' | 'localStorage'): Storage | null {
  try {
    return window[kind];
  } catch {
    return null;
  }
}

purgeLegacyTokenStorage(safeStorage('sessionStorage'), safeStorage('localStorage'));

export const userManager = new UserManager(
  oidcSettings({
    authority,
    clientId,
    origin: window.location.origin,
    stateStorage: safeStorage('sessionStorage') ?? new InMemoryWebStorage(),
    uiLocales: useI18n.getState().locale,
  }),
);

/**
 * R6.13 K-20 — token phiên hỗ trợ (iam-svc ký, ≤ 30 phút, không refresh). CHỈ giữ trong bộ nhớ (không sessionStorage/localStorage):
 * tải lại trang = mất token, xin lại khi phiên còn ACTIVE. Dùng cho API của tenant được hỗ trợ; control plane `/api/v1/platform/**`
 * vẫn đi bằng token platform (để kết thúc/thu hồi phiên).
 */
export interface SupportToken {
  accessToken: string;
  expiresAt: string;
  supportAccess: 'read' | 'write';
  supportSessionId: string;
  targetTenantId: string;
}
let supportToken: SupportToken | null = null;

export function setSupportToken(t: SupportToken | null): void {
  supportToken = t;
}

/** Token phiên còn hạn (xóa khi đã hết exp — không tự làm mới). */
export function activeSupportToken(): SupportToken | null {
  if (supportToken && Date.parse(supportToken.expiresAt) <= Date.now()) supportToken = null;
  return supportToken;
}

const PLATFORM_PREFIX = '/api/v1/platform/';

/**
 * Làm mới bằng refresh token — MỘT lời gọi dù nhiều yêu cầu cùng thấy token hết hạn (máy ngủ, tab bị treo): gọi song song sẽ gửi CÙNG refresh token hai lần,
 * lần thứ hai bị Keycloak coi là tái sử dụng (maxReuse 0) → gỡ phiên + cảnh báo bảo mật giả.
 */
let renewing: Promise<User | null> | null = null;
export function renewOnce(manager: UserManager = userManager): Promise<User | null> {
  if (!renewing) {
    renewing = manager
      .signinSilent()
      .catch(() => null)
      .finally(() => {
        renewing = null;
      });
  }
  return renewing;
}

export async function getAccessToken(path?: string): Promise<string | null> {
  const st = activeSupportToken();
  if (st && !(path ?? '').startsWith(PLATFORM_PREFIX)) return st.accessToken;
  let u = await userManager.getUser();
  if (u?.expired && u.refresh_token) u = await renewOnce();
  if (!u || u.expired) return null;
  return u.access_token;
}

/** Chỉ nhận đường dẫn nội bộ — không cho `returnTo` trỏ ra ngoài (open redirect). */
export function safeReturnTo(path: string | null | undefined): string {
  // 13/10/2026 (rà soát): không quay lại trang callback OIDC (mã/state dùng một lần) — lỗi ngay trên /auth/callback rồi đăng nhập lại sẽ gặp lại "No matching state"
  if (path?.startsWith('/auth/callback')) return '/';
  return path?.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\') ? path : '/';
}

/** Tham số URL mang alias IdP SSO của tenant (liên kết `ssoLoginLink` của api-client: `…/?kc_idp_hint=<alias>`). */
export const IDP_HINT_PARAM = 'kc_idp_hint';
/** Alias IdP tenant do iam-svc sinh: `t-<12 hex cuối tenantId>-<hậu tố 1–24 ký tự a-z0-9->` (TenantIdp.ALIAS) — khớp chặt, không chuyển tiếp giá trị lạ tới Keycloak. */
const IDP_HINT = /^t-[0-9a-f]{12}-[a-z0-9](?:[a-z0-9-]{0,22}[a-z0-9])?$/;

/** T9-F1 (A27): đọc `kc_idp_hint` hợp lệ từ chuỗi truy vấn; sai định dạng/không có → null (đăng nhập thường). */
export function idpHintFrom(search: string | null | undefined): string | null {
  if (!search) return null;
  let v: string | null = null;
  try {
    v = new URLSearchParams(search).get(IDP_HINT_PARAM);
  } catch {
    return null;
  }
  return v && IDP_HINT.test(v) ? v : null;
}

/** Đường quay lại sau đăng nhập — bỏ `kc_idp_hint` (tránh vòng chuyển hướng IdP và không giữ gợi ý trong lịch sử). */
export function returnPathWithoutHint(pathname: string, search: string): string {
  const q = new URLSearchParams(search);
  q.delete(IDP_HINT_PARAM);
  const s = q.toString();
  return pathname + (s ? `?${s}` : '');
}

/** Tham số bổ sung cho `signinRedirect`: có gợi ý IdP → Keycloak chuyển thẳng tới IdP doanh nghiệp (Identity Brokering, realm kovix-tenant). */
export function signinArgsFor(search: string): { extraQueryParams: Record<string, string> } | undefined {
  const hint = idpHintFrom(search);
  return hint ? { extraQueryParams: { [IDP_HINT_PARAM]: hint } } : undefined;
}

export async function ensureSignedIn(): Promise<User> {
  const u = await userManager.getUser();
  if (u && !u.expired) return u;
  if (u?.refresh_token) {
    const renewed = await renewOnce();
    if (renewed && !renewed.expired) return renewed;
  }
  // Lưu đường dẫn sâu để quay lại đúng màn hình sau đăng nhập (deep-link vẫn qua route guard)
  try {
    sessionStorage.setItem(RETURN_TO_KEY, returnPathWithoutHint(window.location.pathname, window.location.search));
  } catch {
    /* storage bị chặn — về trang chủ */
  }
  // T9-F1 (A27): liên kết đăng nhập SSO của tenant mang kc_idp_hint → chuyển thẳng tới IdP doanh nghiệp
  await userManager.signinRedirect(signinArgsFor(window.location.search));
  return new Promise(() => {}); // chuyển hướng — không bao giờ resolve
}

const REAUTH_AT_KEY = 'kovix.reauthAt';
/** Vừa đăng nhập lại trong khoảng này mà API vẫn 401 → coi là lỗi phía máy chủ (realm/quyền), không chuyển hướng nữa. */
const REAUTH_LOOP_MS = 60_000;
let reauthRedirecting = false;

/**
 * 09/10/2026 (staging: 401 ở /platform/config → bị đẩy về Tổng quan không lời giải thích) — 401 từ API: đăng nhập lại và QUAY VỀ ĐÚNG TRANG
 * (ghi `kovix.returnTo` như ensureSignedIn). Nếu vừa đăng nhập lại < 60 s mà vẫn 401 thì KHÔNG chuyển hướng (tránh vòng lặp im lặng khi phiên SSO còn):
 * trả false để ApiError 401 đi tiếp và màn hình hiện lỗi.
 */
export function reauthenticate(): boolean {
  if (reauthRedirecting) return true;
  try {
    const at = Number(sessionStorage.getItem(REAUTH_AT_KEY) ?? 0);
    if (at && Date.now() - at < REAUTH_LOOP_MS) return false;
    sessionStorage.setItem(REAUTH_AT_KEY, String(Date.now()));
    sessionStorage.setItem(RETURN_TO_KEY, returnPathWithoutHint(window.location.pathname, window.location.search));
  } catch {
    /* storage bị chặn — vẫn đăng nhập lại, về trang chủ */
  }
  reauthRedirecting = true;
  void userManager.signinRedirect();
  return true;
}

const CALLBACK_RECOVER_KEY = 'kovix.callbackRecoverAt';

/** oidc-client-ts: "No matching state found in storage" — callback mở lại (Back/F5/link cũ) sau khi state đã dùng/xóa. */
export function isStaleCallbackError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e ?? '');
  return /no matching state found|state not found/i.test(msg);
}

/** true nếu vừa phục hồi callback < 60 s (rồi ghi mốc mới khi chưa) — chống vòng chuyển hướng. */
function recoveredRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(CALLBACK_RECOVER_KEY) ?? 0);
    if (at && Date.now() - at < REAUTH_LOOP_MS) return true;
    sessionStorage.setItem(CALLBACK_RECOVER_KEY, String(Date.now()));
  } catch {
    /* storage bị chặn — vẫn phục hồi một lần */
  }
  return false;
}

export async function completeSignIn(): Promise<string> {
  try {
    await userManager.signinCallback();
  } catch (e) {
    // 12/10/2026 (staging: đăng nhập xong bấm Back → "Something went wrong!") — CHỈ bỏ qua lỗi mở lại /auth/callback với state ĐÃ DÙNG (không còn trong
    // stateStore): về "/" — phiên còn thì vào thẳng app, không thì route guard đăng nhập lại. Lỗi khác (token endpoint 5xx, invalid_grant…) NÉM tiếp
    // cho màn lỗi. Chốt 60 s: vừa phục hồi mà lại lỗi → ném (không vòng chuyển hướng vô hạn với phiên SSO còn sống).
    if (!isStaleCallbackError(e) || recoveredRecently()) throw e;
    console.warn('[KOVIX] bỏ qua callback đăng nhập đã dùng', e);
    try {
      sessionStorage.removeItem(RETURN_TO_KEY);
    } catch {
      /* storage bị chặn */
    }
    return '/';
  }
  let to: string | null = null;
  try {
    to = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* storage bị chặn */
  }
  return safeReturnTo(to);
}

export function signOut() {
  // Quản trị viên nền tảng đăng xuất → quay lại cổng /platform (đăng nhập lại đúng realm); lựa chọn realm của tab xóa ở đó nếu đi nơi khác
  try {
    window.sessionStorage.removeItem(LOGIN_REALM_KEY);
  } catch {
    /* storage bị chặn */
  }
  return loginRealm === 'platform'
    ? userManager.signoutRedirect({ post_logout_redirect_uri: `${window.location.origin}${PLATFORM_ENTRY}` })
    : userManager.signoutRedirect();
}

/** W2 accountProfile — đổi mật khẩu / MFA làm ở Keycloak Account Console (iam-svc không giữ mật khẩu, BR-18.20) — đúng realm của tab. */
export const accountConsoleUrl: string = forRealm(
  import.meta.env.VITE_OIDC_ACCOUNT_URL ?? `${tenantAuthority}/account`,
  loginRealm,
);

export { jwtPayload, MFA_AMR, mfaStateOf } from './mfa';

/** Trạng thái 2FA của phiên hiện tại (access token trong bộ nhớ) — chỉ hiển thị. */
export async function mfaContext(): Promise<{ sessionMfa: boolean; required: boolean }> {
  const u = await userManager.getUser();
  return mfaStateOf({ ...((u?.profile ?? {}) as Record<string, unknown>), ...jwtPayload(u?.access_token) });
}

/** Ngữ cảnh hiển thị (tên, tenant) lấy từ ID token — chỉ để hiển thị, không để phân quyền. */
export async function displayContext(): Promise<{ name: string; email: string; tenantId: string | null }> {
  const u = await userManager.getUser();
  const p = (u?.profile ?? {}) as Record<string, unknown>;
  return {
    name: String(p.name ?? p.preferred_username ?? ''),
    email: String(p.email ?? ''),
    tenantId: typeof p.tenant_id === 'string' ? p.tenant_id : null,
  };
}
